'use client';

import {
	BigNum,
	OrderStatus,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	Serializer,
	UISerializableOrder,
	calculateAverageEntryPrice,
} from '@velocity-exchange/common';
import React, { PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { dlog } from '../../dev';
import useDriftAccountStore from '../../stores/useDriftAccountsStore';
import useNotificationStore from '../../stores/useNotificationStore';
import { MarketOrderToast } from '../../utils/notifications';
import { getMarketPrecision } from '../../utils/market';
import { MarketOrderToastEventContext } from './MarketOrderToastEventEmitterProvider';
import {
	MarketOrderToastId,
	MarketOrderToastOrderInfo,
	MarketOrderToastState,
	MarketOrderToastStateTransitionEvent,
	V3MarketOrderHeadlineStage,
} from './MarketOrderToastStateTypes';

import {
	convertUISerializableOrderToEvent,
	createInitialOrderInfoFromEvent,
	findToastIdForOrder,
	isRelevantMarketOrder,
} from './marketOrderToastUtils';
import useAppEventEmitter from '../../hooks/useAppEventEmitter';
import { OrderRetryButton } from '../Toasts/MarketOrders/OrderRetryButton';
import { TradeFormDetails } from '../TradeConfirmationModal/types';

const END_STATE_DISPLAY_TIME = 5_000;
const STALE_TOAST_TIMEOUT_MS = 60_000; // 60 seconds - cleanup toasts that haven't received events
const STALE_TOAST_CHECK_INTERVAL_MS = 10_000; // Check every 10 seconds

const getToastId = (toastId: MarketOrderToastId) =>
	`market_order_toast_${toastId}`;

const getInitialToastState = (
	orderDetails: MarketOrderToastOrderInfo
): MarketOrderToastState => {
	return {
		v3HeadlineStage: 'awaiting_signature',
		orderInfo: orderDetails,
		dev_data: [],
	};
};

const isV3EndState = (v3Stage: V3MarketOrderHeadlineStage): boolean => {
	return (
		v3Stage === 'filled_and_complete' ||
		v3Stage === 'partial_filled_and_complete' ||
		v3Stage === 'not_filled_and_expired' ||
		v3Stage === 'not_filled_and_cancelled' ||
		v3Stage === 'not_filled_and_error' ||
		v3Stage === 'failed_signature' ||
		v3Stage === 'failed_confirmation'
	);
};

const isOrderCompletelyFilled = (
	orderInfo: MarketOrderToastOrderInfo
): boolean => {
	return orderInfo.cumulativeBaseAmountFilled.eq(orderInfo.baseAmountOrdered);
};

const isOrderPartiallyFilled = (
	orderInfo: MarketOrderToastOrderInfo
): boolean => {
	return (
		orderInfo.cumulativeBaseAmountFilled.gt(ZERO) &&
		orderInfo.cumulativeBaseAmountFilled.lt(orderInfo.baseAmountOrdered)
	);
};

const getFillState = (
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage => {
	if (orderInfo.cumulativeBaseAmountFilled.eq(ZERO)) {
		return 'awaiting_fill';
	}
	return 'filling_partial';
};

const handleEndStateTransition = (
	event: MarketOrderToastStateTransitionEvent,
	previousV3HeadlineStage: V3MarketOrderHeadlineStage,
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage | null => {
	if (!isV3EndState(previousV3HeadlineStage)) {
		return null;
	}

	dlog(
		'market_order_toasts',
		'processNewV3HeadlineStage: already in end state',
		{
			toastId: event.toastId,
			eventType: event.type,
			previousV3HeadlineStage,
		}
	);

	if (event.type === 'order_state_read') {
		if (
			event.data.status &&
			ENUM_UTILS.match(event.data.status, OrderStatus.FILLED)
		) {
			if (isOrderCompletelyFilled(orderInfo)) {
				dlog(
					'market_order_toasts',
					'processNewV3HeadlineStage: end state -> filled_and_complete via order_state_read',
					{ toastId: event.toastId }
				);
				return 'filled_and_complete';
			}
			if (isOrderPartiallyFilled(orderInfo)) {
				dlog(
					'market_order_toasts',
					'processNewV3HeadlineStage: end state -> partial_filled_and_complete via order_state_read',
					{ toastId: event.toastId }
				);
				return 'partial_filled_and_complete';
			}
		}
	}

	dlog(
		'market_order_toasts',
		'processNewV3HeadlineStage: staying in end state (no transition)',
		{
			toastId: event.toastId,
			eventType: event.type,
			previousV3HeadlineStage,
		}
	);
	return previousV3HeadlineStage;
};

const handleOrderStateReadEvent = (
	event: MarketOrderToastStateTransitionEvent,
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage | null => {
	if (event.type !== 'order_state_read') {
		return null;
	}

	if (
		event.data.status &&
		ENUM_UTILS.match(event.data.status, OrderStatus.FILLED)
	) {
		if (isOrderCompletelyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: order_state_read FILLED -> filled_and_complete',
				{ toastId: event.toastId }
			);
			return 'filled_and_complete';
		}
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: order_state_read FILLED -> partial_filled_and_complete',
			{ toastId: event.toastId }
		);
		return 'partial_filled_and_complete';
	}

	if (
		event.data.status &&
		ENUM_UTILS.match(event.data.status, OrderStatus.CANCELED)
	) {
		if (isOrderPartiallyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: order_state_read CANCELED -> partial_filled_and_complete',
				{ toastId: event.toastId }
			);
			return 'partial_filled_and_complete';
		}
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: order_state_read CANCELED -> not_filled_and_cancelled',
			{ toastId: event.toastId }
		);
		return 'not_filled_and_cancelled';
	}

	const orderIsCompletelyFilled =
		event.data.baseAmountOrdered && event.data.cumulativeBaseAmountFilled
			? event.data.cumulativeBaseAmountFilled.eq(event.data.baseAmountOrdered)
			: false;
	if (orderIsCompletelyFilled) {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: order_state_read (from data) -> filled_and_complete',
			{ toastId: event.toastId }
		);
		return 'filled_and_complete';
	}

	if (event.data.auctionEnded) {
		return getFillState(orderInfo);
	}

	return getFillState(orderInfo);
};

const handleCancellationExpirationEvent = (
	event: MarketOrderToastStateTransitionEvent,
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage | null => {
	if (event.type === 'action_cancelled') {
		if (isOrderPartiallyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: action_cancelled -> partial_filled_and_complete',
				{ toastId: event.toastId }
			);
			return 'partial_filled_and_complete';
		}
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: action_cancelled -> not_filled_and_cancelled',
			{ toastId: event.toastId }
		);
		return 'not_filled_and_cancelled';
	}

	// TODO: we got one of these events before the order got filled?? check on that
	if (event.type === 'action_expired') {
		if (isOrderPartiallyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: action_expired -> partial_filled_and_complete',
				{ toastId: event.toastId }
			);
			return 'partial_filled_and_complete';
		}
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: action_expired -> not_filled_and_expired',
			{ toastId: event.toastId }
		);
		return 'not_filled_and_expired';
	}

	return null;
};

const handleAwaitingSignatureTransition = (
	event: MarketOrderToastStateTransitionEvent,
	previousV3HeadlineStage: V3MarketOrderHeadlineStage
): V3MarketOrderHeadlineStage | null => {
	if (previousV3HeadlineStage !== 'awaiting_signature') {
		return null;
	}

	if (event.type === 'tx_signed') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: awaiting_signature -> awaiting_confirmation',
			{ toastId: event.toastId }
		);
		return 'awaiting_confirmation';
	}
	if (event.type === 'tx_failed') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: awaiting_signature -> failed_signature',
			{ toastId: event.toastId }
		);
		return 'failed_signature';
	}
	if (event.type === 'signed_msg_order_message_slot_expired') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: awaiting_signature -> not_filled_and_expired (message slot)',
			{ toastId: event.toastId }
		);
		return 'not_filled_and_expired';
	}

	return previousV3HeadlineStage;
};

const handleAwaitingConfirmationTransition = (
	event: MarketOrderToastStateTransitionEvent,
	previousV3HeadlineStage: V3MarketOrderHeadlineStage,
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage | null => {
	if (previousV3HeadlineStage !== 'awaiting_confirmation') {
		return null;
	}

	const eventType = event.type;

	if (
		eventType === 'receive_confirmation' ||
		eventType === 'receive_confirmation_signed_msg_order' ||
		eventType === 'receive_fill_action'
	) {
		return getFillState(orderInfo);
	}

	if (eventType === 'receive_order_record') {
		if (isOrderCompletelyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: awaiting_confirmation -> filled_and_complete (order_record)',
				{ toastId: event.toastId }
			);
			return 'filled_and_complete';
		}
		return getFillState(orderInfo);
	}

	if (eventType === 'tx_failed') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: awaiting_confirmation -> failed_confirmation',
			{ toastId: event.toastId }
		);
		return 'failed_confirmation';
	}

	if (eventType === 'received_confirmation_and_auction_disabled') {
		if (isOrderCompletelyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: awaiting_confirmation -> filled_and_complete (auction disabled)',
				{ toastId: event.toastId }
			);
			return 'filled_and_complete';
		}
		if (isOrderPartiallyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: awaiting_confirmation -> filling_partial (auction disabled)',
				{ toastId: event.toastId }
			);
			return 'filling_partial';
		}
		return 'awaiting_fill';
	}

	if (eventType === 'order_completely_filled') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: awaiting_confirmation -> filled_and_complete',
			{ toastId: event.toastId }
		);
		return 'filled_and_complete';
	}

	if (eventType === 'signed_msg_order_expired') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: awaiting_confirmation -> not_filled_and_expired',
			{ toastId: event.toastId }
		);
		return 'not_filled_and_expired';
	}

	return previousV3HeadlineStage;
};

const handleFillingTransition = (
	event: MarketOrderToastStateTransitionEvent,
	previousV3HeadlineStage: V3MarketOrderHeadlineStage,
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage | null => {
	if (
		previousV3HeadlineStage !== 'awaiting_fill' &&
		previousV3HeadlineStage !== 'filling_partial'
	) {
		return null;
	}

	const eventType = event.type;

	if (eventType === 'order_completely_filled') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: filling -> filled_and_complete',
			{ toastId: event.toastId, previousV3HeadlineStage }
		);
		return 'filled_and_complete';
	}

	if (eventType === 'auction_finished' || eventType === 'receive_fill_action') {
		return getFillState(orderInfo);
	}

	if (eventType === 'tx_failed') {
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: filling -> not_filled_and_error',
			{ toastId: event.toastId, previousV3HeadlineStage }
		);
		return 'not_filled_and_error';
	}

	if (eventType === 'signed_msg_order_expired') {
		if (isOrderPartiallyFilled(orderInfo)) {
			dlog(
				'market_order_toasts',
				'processNewV3HeadlineStage: filling -> partial_filled_and_complete (expired)',
				{ toastId: event.toastId, previousV3HeadlineStage }
			);
			return 'partial_filled_and_complete';
		}
		dlog(
			'market_order_toasts',
			'processNewV3HeadlineStage: filling -> not_filled_and_expired',
			{ toastId: event.toastId, previousV3HeadlineStage }
		);
		return 'not_filled_and_expired';
	}

	return previousV3HeadlineStage;
};

/**
 * Determines the new v3 headline stage based on the event and current state.
 *
 * Handles state transitions for market order toasts, including end state handling,
 * order state reads, cancellations/expirations, and stage-specific transitions.
 *
 * @param event - The state transition event
 * @param previousV3HeadlineStage - The current v3 headline stage
 * @param orderInfo - The order information used to determine fill state
 * @returns The new v3 headline stage
 */
const processNewV3HeadlineStage = (
	event: MarketOrderToastStateTransitionEvent,
	previousV3HeadlineStage: V3MarketOrderHeadlineStage,
	orderInfo: MarketOrderToastOrderInfo
): V3MarketOrderHeadlineStage => {
	const endStateResult = handleEndStateTransition(
		event,
		previousV3HeadlineStage,
		orderInfo
	);
	if (endStateResult !== null) {
		return endStateResult;
	}

	const orderStateReadResult = handleOrderStateReadEvent(event, orderInfo);
	if (orderStateReadResult !== null) {
		return orderStateReadResult;
	}

	const cancellationExpirationResult = handleCancellationExpirationEvent(
		event,
		orderInfo
	);
	if (cancellationExpirationResult !== null) {
		return cancellationExpirationResult;
	}

	const awaitingSignatureResult = handleAwaitingSignatureTransition(
		event,
		previousV3HeadlineStage
	);
	if (awaitingSignatureResult !== null) {
		return awaitingSignatureResult;
	}

	const awaitingConfirmationResult = handleAwaitingConfirmationTransition(
		event,
		previousV3HeadlineStage,
		orderInfo
	);
	if (awaitingConfirmationResult !== null) {
		return awaitingConfirmationResult;
	}

	const fillingResult = handleFillingTransition(
		event,
		previousV3HeadlineStage,
		orderInfo
	);
	if (fillingResult !== null) {
		return fillingResult;
	}

	return previousV3HeadlineStage;
};

const calculateOrderInfo = (
	currentOrderInfo: MarketOrderToastOrderInfo,
	event: MarketOrderToastStateTransitionEvent
): MarketOrderToastOrderInfo => {
	const previousBaseFilled = currentOrderInfo.cumulativeBaseAmountFilled;
	const previousQuoteFilled = currentOrderInfo.cumulativeQuoteAmountFilled;

	const newOrderInfo: MarketOrderToastOrderInfo = {
		...currentOrderInfo,
		...event.data,
		orderId: event.data?.orderId ?? currentOrderInfo.orderId,
		baseAmountOrdered:
			event.data?.baseAmountOrdered ?? currentOrderInfo.baseAmountOrdered,
		cumulativeQuoteAmountFilled: event.data?.cumulativeQuoteAmountFilled?.gt(
			currentOrderInfo.cumulativeQuoteAmountFilled
		)
			? event.data.cumulativeQuoteAmountFilled
			: currentOrderInfo.cumulativeQuoteAmountFilled,
		cumulativeBaseAmountFilled: event.data?.cumulativeBaseAmountFilled?.gt(
			currentOrderInfo.cumulativeBaseAmountFilled
		)
			? event.data.cumulativeBaseAmountFilled
			: currentOrderInfo.cumulativeBaseAmountFilled,
	};

	const baseFilledChanged =
		!newOrderInfo.cumulativeBaseAmountFilled.eq(previousBaseFilled);
	const quoteFilledChanged =
		!newOrderInfo.cumulativeQuoteAmountFilled.eq(previousQuoteFilled);

	if (baseFilledChanged || quoteFilledChanged) {
		dlog('market_order_toasts', 'calculateOrderInfo: fill amounts updated', {
			toastId: event.toastId,
			eventType: event.type,
			previousBaseFilled: previousBaseFilled.toString(),
			newBaseFilled: newOrderInfo.cumulativeBaseAmountFilled.toString(),
			previousQuoteFilled: previousQuoteFilled.toString(),
			newQuoteFilled: newOrderInfo.cumulativeQuoteAmountFilled.toString(),
		});
	}

	const avgFillPrice =
		newOrderInfo.cumulativeQuoteAmountFilled &&
		newOrderInfo.cumulativeBaseAmountFilled
			? newOrderInfo.cumulativeQuoteAmountFilled.eq(ZERO)
				? '-'
				: calculateAverageEntryPrice(
						BigNum.from(
							newOrderInfo.cumulativeQuoteAmountFilled,
							QUOTE_PRECISION_EXP
						),
						BigNum.from(
							newOrderInfo.cumulativeBaseAmountFilled,
							getMarketPrecision(
								MarketId.getMarketIdFromKey(newOrderInfo.marketId.key)
							)
						)
					).toNotional(true)
			: undefined;

	newOrderInfo.avgFillPrice = avgFillPrice
		? `Avg. fill price: ${avgFillPrice}`
		: undefined;
	newOrderInfo.avgFillPriceNotionalString = avgFillPrice;

	const pctFilled =
		newOrderInfo.baseAmountOrdered &&
		!newOrderInfo.baseAmountOrdered.eq(ZERO) &&
		newOrderInfo.cumulativeBaseAmountFilled
			? newOrderInfo.cumulativeBaseAmountFilled
					.muln(100)
					.div(newOrderInfo.baseAmountOrdered)
			: undefined;
	newOrderInfo.pctFilled = pctFilled
		? `${pctFilled.toString()}% filled`
		: undefined;

	return newOrderInfo;
};

/**
 * Provider component that synchronizes market order toast state with events and user account updates.
 *
 * Listens to state transition events from MarketOrderToastEventContext and processes them to
 * update toast states. Also monitors user account order updates to keep toast states in sync
 * with the actual order state. Automatically manages toast lifecycle including cleanup of
 * end-state timeouts.
 *
 * ## State Machine Flow
 *
 * The state machine manages the lifecycle of market order toasts through the following states and transitions:
 *
 * ### States
 * - **Initial States:**
 *   - `awaiting_signature` - Initial state, waiting for transaction signature
 *   - `awaiting_confirmation` - Waiting for transaction confirmation on-chain
 *   - `awaiting_fill` - Waiting for order to be filled (no fills yet)
 *   - `filling_partial` - Order is being filled (has partial fills)
 *
 * - **Error States:**
 *   - `failed_signature` - Transaction signature failed
 *   - `failed_confirmation` - Transaction confirmation failed
 *   - `not_filled_and_error` - Order failed during filling
 *
 * - **End States (auto-removed after 5s):**
 *   - `filled_and_complete` - Order completely filled
 *   - `partial_filled_and_complete` - Order partially filled and completed/cancelled
 *   - `not_filled_and_expired` - Order expired without fills
 *   - `not_filled_and_cancelled` - Order cancelled without fills
 *
 * ### Valid State Transitions
 *
 * ```
 * awaiting_signature
 *   ├─ tx_signed → awaiting_confirmation
 *   ├─ tx_failed → failed_signature
 *   └─ signed_msg_order_message_slot_expired → not_filled_and_expired
 *
 * awaiting_confirmation
 *   ├─ receive_confirmation → awaiting_fill | filling_partial (based on fill state)
 *   ├─ receive_confirmation_signed_msg_order → awaiting_fill | filling_partial
 *   ├─ receive_fill_action → awaiting_fill | filling_partial
 *   ├─ receive_order_record → filled_and_complete (if completely filled) | awaiting_fill | filling_partial
 *   ├─ received_confirmation_and_auction_disabled → filled_and_complete (if completely filled) | filling_partial (if partially filled) | awaiting_fill
 *   ├─ order_completely_filled → filled_and_complete
 *   ├─ tx_failed → failed_confirmation
 *   └─ signed_msg_order_expired → not_filled_and_expired
 *
 * awaiting_fill
 *   ├─ order_completely_filled → filled_and_complete
 *   ├─ auction_finished → awaiting_fill | filling_partial (based on fill state)
 *   ├─ receive_fill_action → awaiting_fill | filling_partial
 *   ├─ tx_failed → not_filled_and_error
 *   └─ signed_msg_order_expired → not_filled_and_expired (if no fills) | partial_filled_and_complete (if partially filled)
 *
 * filling_partial
 *   ├─ order_completely_filled → filled_and_complete
 *   ├─ auction_finished → awaiting_fill | filling_partial (based on fill state)
 *   ├─ receive_fill_action → awaiting_fill | filling_partial
 *   ├─ tx_failed → not_filled_and_error
 *   └─ signed_msg_order_expired → partial_filled_and_complete
 *
 * action_cancelled
 *   ├─ (if partially filled) → partial_filled_and_complete
 *   └─ (if not filled) → not_filled_and_cancelled
 *
 * action_expired
 *   ├─ (if partially filled) → partial_filled_and_complete
 *   └─ (if not filled) → not_filled_and_expired
 *
 * order_state_read (can transition from any state)
 *   ├─ status=FILLED + completely filled → filled_and_complete
 *   ├─ status=FILLED + partially filled → partial_filled_and_complete
 *   ├─ status=CANCELED + partially filled → partial_filled_and_complete
 *   ├─ status=CANCELED + not filled → not_filled_and_cancelled
 *   └─ (other cases) → awaiting_fill | filling_partial (based on fill state)
 * ```
 *
 * ### Special Behaviors
 * - End states automatically remove the toast after 5 seconds (END_STATE_DISPLAY_TIME)
 * - Order state reads from user account can trigger transitions from any state
 * - Fill state (awaiting_fill vs filling_partial) is determined by comparing cumulativeBaseAmountFilled to baseAmountOrdered
 * - If an order disappears from user account while toast is not in end state, it's marked as FILLED
 *
 * @param props - Component props containing children to render
 */
export const MarketOrderToastSyncProvider = (props: PropsWithChildren) => {
	const notificationStore = useNotificationStore();
	const eventContext = useContext(MarketOrderToastEventContext);
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const userAccount = useDriftAccountStore((s) =>
		currentUserKey ? s.accounts[currentUserKey] : undefined
	);
	const appEventEmitter = useAppEventEmitter();

	const toastStateMap = useRef<Map<MarketOrderToastId, MarketOrderToastState>>(
		new Map()
	);
	const endStateTimeouts = useRef<Map<MarketOrderToastId, Timer>>(new Map());
	const lastEventTimestampMap = useRef<Map<MarketOrderToastId, number>>(
		new Map()
	);

	const updateNotificationStore = (
		toastState: MarketOrderToastState,
		source?: string
	) => {
		const id = getToastId(toastState.orderInfo.toastId);
		const notification: MarketOrderToast = {
			...toastState,
			type: 'marketOrder',
			id,
		};
		dlog('market_order_toasts', 'updateNotificationStore', {
			toastId: toastState.orderInfo.toastId,
			orderId: toastState.orderInfo.orderId,
			v3HeadlineStage: toastState.v3HeadlineStage,
			source,
		});
		notificationStore.updateOrAddNotificationById(id, notification, source);
	};

	const removeToast = (toastId: MarketOrderToastId) => {
		dlog('market_order_toasts', 'removeToast', { toastId });
		notificationStore.removeNotificationById(getToastId(toastId));
		const timeout = endStateTimeouts.current.get(toastId);
		if (timeout) {
			clearTimeout(timeout);
			endStateTimeouts.current.delete(toastId);
		}
		toastStateMap.current.delete(toastId);
		lastEventTimestampMap.current.delete(toastId);
		appEventEmitter.emit('marketOrderToastRemoved', toastId);
	};

	/**
	 * Processes a market order toast state transition event.
	 *
	 * For init events, creates a new toast state. For other events, updates the existing
	 * toast state by calculating new order info and determining headline stage transitions.
	 * Automatically sets up timeouts to remove toasts when they reach end states
	 * (completed, cancelled, expired, error).
	 *
	 * @param event - The state transition event to process
	 */
	const processEvent = (event: MarketOrderToastStateTransitionEvent) => {
		const toastId = event.toastId;

		// Track when this toast last received an event for stale toast cleanup
		lastEventTimestampMap.current.set(toastId, Date.now());

		dlog('market_order_toasts', 'processEvent', {
			toastId,
			eventType: event.type,
			orderId: event.data?.orderId,
			userOrderId: event.data?.userOrderId,
		});
		let cachedToastState = toastStateMap.current.get(toastId);
		if (event.type === 'init' || event.type === 'signed_msg_order_init') {
			if (cachedToastState) {
				dlog(
					'market_order_toasts',
					'processEvent: ignoring duplicate init event',
					{ toastId }
				);
				return;
			}

			const initialOrderInfo = createInitialOrderInfoFromEvent(event, toastId);

			cachedToastState = getInitialToastState(initialOrderInfo);
			toastStateMap.current.set(toastId, cachedToastState);
			dlog('market_order_toasts', 'processEvent: created new toast', {
				toastId,
				orderId: initialOrderInfo.orderId,
				userOrderId: initialOrderInfo.userOrderId,
				marketId: initialOrderInfo.marketId.key,
			});
			updateNotificationStore(cachedToastState, 'events_server');
			return;
		}

		if (!cachedToastState) {
			dlog(
				'market_order_toasts',
				'processEvent: no toast state found, ignoring event',
				{ toastId, eventType: event.type }
			);
			return;
		}

		const newOrderInfo = calculateOrderInfo(cachedToastState.orderInfo, event);
		const v3HeadlineStage = processNewV3HeadlineStage(
			event,
			cachedToastState.v3HeadlineStage,
			newOrderInfo
		);

		const stateChanged = v3HeadlineStage !== cachedToastState.v3HeadlineStage;
		if (stateChanged) {
			dlog('market_order_toasts', 'processEvent: state transition', {
				toastId,
				eventType: event.type,
				previousV3HeadlineStage: cachedToastState.v3HeadlineStage,
				newV3HeadlineStage: v3HeadlineStage,
			});
		}

		const newToastState: MarketOrderToastState = {
			...cachedToastState,
			orderInfo: newOrderInfo,
			v3HeadlineStage,
			toastActionContent:
				v3HeadlineStage === 'not_filled_and_expired' &&
				cachedToastState.orderInfo.orderRoute === 'signedMsg' &&
				cachedToastState.orderInfo.originalTradeFormDetails
					? (() => {
							const orderInfo = cachedToastState.orderInfo;
							if (!orderInfo) return null;
							const tradeFormDetails = {
								...orderInfo.originalTradeFormDetails,
								market: orderInfo.marketId,
							} as TradeFormDetails;
							return (
								<div className="flex justify-start">
									<OrderRetryButton tradeFormDetails={tradeFormDetails} />
								</div>
							);
						})()
					: cachedToastState.toastActionContent,
		};

		toastStateMap.current.set(toastId, newToastState);
		const source =
			event.type === 'order_state_read'
				? 'user_account_orders'
				: 'events_server';
		updateNotificationStore(newToastState, source);
		const isEndState = isV3EndState(v3HeadlineStage);

		if (isEndState) {
			const existingTimeout = endStateTimeouts.current.get(toastId);
			if (existingTimeout) {
				dlog(
					'market_order_toasts',
					'processEvent: clearing existing end state timeout',
					{
						toastId,
						v3HeadlineStage,
					}
				);
				clearTimeout(existingTimeout);
			}
			dlog('market_order_toasts', 'processEvent: setting end state timeout', {
				toastId,
				v3HeadlineStage,
				timeoutMs: END_STATE_DISPLAY_TIME,
			});
			const timeout = setTimeout(() => {
				removeToast(toastId);
			}, END_STATE_DISPLAY_TIME);
			endStateTimeouts.current.set(toastId, timeout);
		}
	};

	const processUserAccountOrder = (order: UISerializableOrder) => {
		if (!isRelevantMarketOrder(order)) {
			dlog(
				'market_order_toasts',
				'processUserAccountOrder not relevant order',
				order.orderId
			);
			return;
		}

		const userAccountData = userAccount?.client;
		if (!userAccountData) {
			dlog(
				'market_order_toasts',
				'processUserAccountOrder no user account data',
				order.orderId
			);
			return;
		}

		const userPubkey = userAccountData.getUserAccountOrThrow().authority;
		const marketId = new MarketId(order.marketIndex, order.marketType);
		const toastId = findToastIdForOrder(order, userPubkey);

		if (!toastId) {
			dlog(
				'market_order_toasts',
				'processUserAccountOrder no toastId',
				order.orderId
			);
			return;
		}

		const event = convertUISerializableOrderToEvent(order, marketId, toastId);

		dlog('market_order_toasts', 'processUserAccountOrder: processing order', {
			toastId,
			orderId: order.orderId,
			userOrderId: order.userOrderId,
			status: order.status,
			marketId: marketId.key,
		});

		processEvent(event);
	};

	useEffect(() => {
		dlog(
			'market_order_toasts',
			'useEffect: subscribing to eventContext.subject'
		);
		const subscription = eventContext.subject.subscribe((event) => {
			processEvent(event);
		});

		return () => {
			dlog(
				'market_order_toasts',
				'useEffect: unsubscribing from eventContext.subject'
			);
			subscription.unsubscribe();
		};
	}, [eventContext.subject]);

	useEffect(() => {
		if (!userAccount?.client) {
			return;
		}

		const user = userAccount.client;

		const handleUserUpdate = () => {
			const orders = user.getUserAccountOrThrow().orders;
			const marketOrders = orders
				.map((order) => {
					return Serializer.Deserialize.UIOrder(
						Serializer.Serialize.Order(order)
					);
				})
				.filter(isRelevantMarketOrder);

			const activeToastIds = new Set(toastStateMap.current.keys());

			dlog('market_order_toasts', 'handleUserUpdate', {
				totalOrders: orders.length,
				marketOrders: marketOrders.length,
				activeToasts: activeToastIds.size,
			});

			for (const order of marketOrders) {
				processUserAccountOrder(order);
			}

			for (const toastId of activeToastIds) {
				const toastState = toastStateMap.current.get(toastId);
				if (!toastState) continue;

				const orderId = toastState.orderInfo.orderId;
				if (!orderId || orderId === 0) continue;

				const orderStillExists = marketOrders.some(
					(order) => order.orderId === orderId
				);

				if (!orderStillExists) {
					if (toastState && !isV3EndState(toastState.v3HeadlineStage)) {
						dlog(
							'market_order_toasts',
							'handleUserUpdate: order disappeared, toast stage is in an endable state, marking as FILLED',
							{
								toastId,
								orderId,
								currentV3HeadlineStage: toastState.v3HeadlineStage,
							}
						);
						const event: MarketOrderToastStateTransitionEvent = {
							type: 'order_state_read',
							toastId,
							data: {
								status: OrderStatus.FILLED,
							},
						};
						processEvent(event);
					}
				}
			}
		};

		handleUserUpdate();
		dlog(
			'market_order_toasts',
			'useEffect: subscribing to userAccountUpdate events',
			{
				currentUserKey,
			}
		);
		user.eventEmitter.on('userAccountUpdate', handleUserUpdate);

		return () => {
			dlog(
				'market_order_toasts',
				'useEffect: unsubscribing from userAccountUpdate events'
			);
			user.eventEmitter.removeListener('userAccountUpdate', handleUserUpdate);
		};
	}, [userAccount?.client, currentUserKey]);

	useEffect(() => {
		return () => {
			const timeoutCount = endStateTimeouts.current.size;
			if (timeoutCount > 0) {
				dlog(
					'market_order_toasts',
					'useEffect: cleaning up end state timeouts',
					{
						timeoutCount,
					}
				);
			}
			for (const timeout of endStateTimeouts.current.values()) {
				clearTimeout(timeout);
			}
		};
	}, []);

	// Stale toast cleanup - handles cases where events are missed due to RPC issues, etc.
	useEffect(() => {
		const intervalId = setInterval(() => {
			const now = Date.now();

			for (const [toastId, toastState] of toastStateMap.current.entries()) {
				// Skip if already in end state (will be cleaned up by existing timeout)
				if (isV3EndState(toastState.v3HeadlineStage)) continue;

				const lastEventTime = lastEventTimestampMap.current.get(toastId);
				if (!lastEventTime) continue;

				const timeSinceLastEvent = now - lastEventTime;

				if (timeSinceLastEvent > STALE_TOAST_TIMEOUT_MS) {
					const orderInfo = toastState.orderInfo;
					const orderId = orderInfo.orderId;

					dlog('market_order_toasts', 'stale toast detected', {
						toastId,
						timeSinceLastEvent,
						v3HeadlineStage: toastState.v3HeadlineStage,
						orderId,
					});

					// If no orderId, we can't reliably check order state - transition to error
					if (!orderId || orderId === 0) {
						dlog(
							'market_order_toasts',
							'stale toast cleanup: no orderId, transitioning to error',
							{ toastId }
						);
						processEvent({
							type: 'tx_failed',
							toastId,
							data: {
								failureMessage: 'Order timed out - please check your positions',
							},
						});
						continue;
					}

					// We have an orderId - check if order still exists in user account
					const user = userAccount?.client;
					if (!user) {
						dlog(
							'market_order_toasts',
							'stale toast cleanup: no user client, transitioning to error',
							{ toastId }
						);
						processEvent({
							type: 'tx_failed',
							toastId,
							data: {
								failureMessage: 'Order timed out - please check your positions',
							},
						});
						continue;
					}

					const orders = user.getUserAccountOrThrow().orders;
					const uiOrders = orders.map((order) =>
						Serializer.Deserialize.UIOrder(Serializer.Serialize.Order(order))
					);

					// Look for matching order by orderId, marketIndex, and marketType
					const matchingOrder = uiOrders.find(
						(order) =>
							order.orderId === orderId &&
							order.marketIndex === orderInfo.marketId.marketIndex &&
							ENUM_UTILS.match(order.marketType, orderInfo.marketId.marketType)
					);

					if (matchingOrder) {
						// Order still exists - process its current state
						dlog(
							'market_order_toasts',
							'stale toast cleanup: found matching order, processing state',
							{
								toastId,
								orderId,
								orderStatus: matchingOrder.status,
							}
						);
						const marketId = new MarketId(
							matchingOrder.marketIndex,
							matchingOrder.marketType
						);
						const event = convertUISerializableOrderToEvent(
							matchingOrder,
							marketId,
							toastId
						);
						processEvent(event);
					} else {
						// Order no longer exists - it completed (filled/cancelled)
						dlog(
							'market_order_toasts',
							'stale toast cleanup: order not found, marking as filled',
							{ toastId, orderId }
						);
						processEvent({
							type: 'order_state_read',
							toastId,
							data: {
								status: OrderStatus.FILLED,
							},
						});
					}
				}
			}
		}, STALE_TOAST_CHECK_INTERVAL_MS);

		return () => clearInterval(intervalId);
	}, [userAccount?.client]);

	return <>{props.children}</>;
};
